import React, { useEffect, useState } from "react";
import moment from "moment-timezone";

import { getGuestSpecialEvents } from "../../services/client";
import { getMockSpecialEvents } from "./mockEvents";
import {
  applyEventFilters,
  spotsLeft,
  DATE_RANGE_OPTIONS,
} from "./eventFilters";

const SERVICE_LABEL = "Professional Demonstration";

const fetchEvents = (params) =>
  process.env.REACT_APP_USE_MOCK_SPECIAL_EVENTS === "true"
    ? getMockSpecialEvents(params)
    : getGuestSpecialEvents(params);

const eventMoment = (event, time) =>
  moment(`${event.date} ${time}`, ["YYYY-MM-DD HH:mm", "YYYY-MM-DD hh:mm A"]);

const formatPrice = (event) =>
  `$${Number(event.price)} ${event.currency || "USD"}`;

const languagesLabel = (event) =>
  Array.isArray(event.languages) ? event.languages.join(", ") : event.languages;

export default function EventList({ selection, onChangeTimezone, onBook, mediumId, availabilityId }) {
  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState("");
  const [range, setRange] = useState("all");
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [now, setNow] = useState(moment());

  // Current-time ticker in the guest's chosen timezone
  useEffect(() => {
    const interval = setInterval(() => setNow(moment()), 1000);
    return () => clearInterval(interval);
  }, []);

  const currentTime = selection.timeZoneLocalName
    ? moment(now).tz(selection.timeZoneLocalName).format("hh:mm:ss A")
    : moment(now).format("hh:mm:ss A");

  const buildParams = () => {
    const params = { timezone: selection.timeZoneValue };
    if (mediumId) params.medium_id = mediumId;
    if (availabilityId) params.availability_id = availabilityId;
    if (search.trim()) params.search = search.trim();
    if (range !== "all") {
      params.from = moment().format("YYYY-MM-DD");
      params.to =
        range === "this_month"
          ? moment().endOf("month").format("YYYY-MM-DD")
          : moment().add(parseInt(range, 10), "days").format("YYYY-MM-DD");
    }
    return params;
  };

  // Fetch on mount and when filters change (search debounced). Filters are
  // passed to the API and also re-applied client-side as a fallback.
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoadError(false);
      fetchEvents(buildParams())
        .then((data) => {
          setLoaded(true);
          if (data && data.data && data.data.status) {
            setEvents(Array.isArray(data.data.data) ? data.data.data : []);
          } else {
            setEvents([]);
            setLoadError(true);
          }
        })
        .catch(() => {
          setLoaded(true);
          setEvents([]);
          setLoadError(true);
        });
    }, search ? 400 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, range]);

  const resetFilters = () => {
    setSearch("");
    setRange("all");
  };

  const visibleEvents = applyEventFilters({ events, search, range });

  return (
    <div className="container se-list">
      <div className="text-center">
        <span className="se-time-pill">Current Time {currentTime}</span>
      </div>
      <div className="text-center mt-3 se-tz-row">
        <h5 className="font-weight-bold d-inline-block mb-0">
          Timezone: {selection.timeZoneName}
        </h5>
        <button type="button" className="se-change-link" onClick={onChangeTimezone}>
          change
        </button>
      </div>

      <div className="se-filter-row mt-4">
        <label className="se-filter-label">Filter:</label>
        <input
          type="text"
          className="se-search-input"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="se-range-select pointer"
          value={range}
          onChange={(e) => setRange(e.target.value)}
        >
          {DATE_RANGE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.value === "all" ? "Select date range" : option.label}
            </option>
          ))}
        </select>
        <button type="button" className="se-btn-tan se-reset-btn" onClick={resetFilters}>
          Reset
        </button>
      </div>

      <div className="se-events-header mt-5">
        <h4>Events</h4>
        <p className="se-muted mb-0">Sessions that are full are not displayed</p>
      </div>

      {loaded && visibleEvents.length === 0 && (
        <div className="se-empty mt-4">
          <p className="mb-0">
            {loadError
              ? "We couldn't load events right now. Please try again in a few minutes."
              : "No upcoming events found. Please check back soon."}
          </p>
        </div>
      )}

      {visibleEvents.map((event, index) => {
        const start = eventMoment(event, event.start_time);
        const end = eventMoment(event, event.end_time);
        const free = !(Number(event.price) > 0);
        const expanded = expandedId === event.event_id;
        return (
          <div
            key={event.event_id}
            className={`se-event-card ${index % 2 === 0 ? "se-card-peach" : ""}`}
          >
            <div className="se-card-left">
              <div className="se-date-badge">
                <span className="se-date-month">{start.format("MMM").toUpperCase()}</span>
                <span className="se-date-day">{start.format("DD")}</span>
              </div>
              <div className="se-card-info">
                <div className="se-card-line1">
                  {start.format("ddd")} &middot; {start.format("h:mm A")} &ndash;{" "}
                  {end.format("h:mm A")} ({SERVICE_LABEL})
                </div>
                <div className="se-card-title">
                  {event.title}
                  {languagesLabel(event) ? ` (${languagesLabel(event)})` : ""}
                </div>
                <div className="se-card-details">
                  <button
                    type="button"
                    className="se-more-details"
                    onClick={() => setExpandedId(expanded ? null : event.event_id)}
                  >
                    More Details &#8811;
                  </button>
                  {expanded && (
                    <div
                      className="se-description mb-0"
                      dangerouslySetInnerHTML={{ __html: event.description }}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="se-card-right">
              <div className="se-spots">
                {spotsLeft(event)} spots left (of {event.capacity})
              </div>
              <div className="se-book-row">
                <button
                  type="button"
                  className="se-book-btn"
                  onClick={() => onBook(event)}
                >
                  {free ? "Book Here" : `Book - ${formatPrice(event)} ≫`}
                </button>
                {free && <span className="se-free-note">(free)</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
